import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight, ShieldCheck } from "lucide-react"
import { toast } from "sonner"
import type { ConversationSummary } from "@vienna/contracts"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { PageHeading } from "@/components/app/page-heading"
import { api, ApiError } from "@/lib/api"
import { formatDateTime, initials, relativeTime } from "@/lib/format"
import { handoversQuery, teamQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/handovers")({
  loader: ({ context, params }) => Promise.all([context.queryClient.ensureQueryData(handoversQuery(params.workspace)), context.queryClient.ensureQueryData(teamQuery(params.workspace))]),
  component: Handover,
})

/** Screen 12. */
function Handover() {
  const { workspace, membershipId } = useWorkspaceContext() as unknown as { workspace: { slug: string }; membershipId?: string }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { items } = useSuspenseQuery(handoversQuery(workspace.slug)).data
  const team = useSuspenseQuery(teamQuery(workspace.slug)).data
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = items.find((i) => i.conversation.id === selectedId) ?? items[0] ?? null
  const [assignee, setAssignee] = useState<string>(membershipId ?? team.members[0]?.id ?? "")
  const [status, setStatus] = useState<"open" | "pending">("open")
  const [note, setNote] = useState("")
  const params = { workspace: workspace.slug }
  const accept = useMutation({
    mutationFn: () => api<ConversationSummary>(`/w/${workspace.slug}/conversations/${selected!.conversation.id}/handover/accept`, { method: "POST", body: JSON.stringify({ assigneeMembershipId: assignee, statusAfter: status, ...(note.trim() ? { note: note.trim() } : {}) }) }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug] })
      toast.success(`Handover accepted by ${c.assigneeName ?? "you"}`)
      await navigate({ to: "/w/$workspace/inbox", params, search: { c: c.id } })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not accept the handover"),
  })

  return (
    <div className="page-wrap">
      <PageHeading title="A person makes the difference" description="Review the journey, pick an owner, and continue the conversation." />
      <div className="handover-layout">
        <Card className="handover-queue">
          <CardHeader>
            <CardTitle>
              Human handovers{" "}
              <Badge variant="secondary" className="ml-2">
                {items.length}
              </Badge>
            </CardTitle>
            <CardDescription className="text-xs">Waiting for a person</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {items.map((item) => (
              <button key={item.conversation.id} className={`handover-person ${selected?.conversation.id === item.conversation.id ? "selected" : ""}`} onClick={() => setSelectedId(item.conversation.id)}>
                <Avatar className="size-8">
                  <AvatarFallback className="text-[11px] bg-stone-200 text-stone-700">{initials(item.conversation.contactName)}</AvatarFallback>
                </Avatar>
                <span>
                  <strong>{item.conversation.contactName}</strong>
                  <small>{item.reason}</small>
                  <small>
                    {item.conversation.provider === "instagram" ? "Instagram" : "Messenger"} · Waiting {relativeTime(item.waitingSince).toLowerCase()}
                  </small>
                </span>
              </button>
            ))}
            {items.length === 0 && <p className="text-sm text-muted-foreground">Nobody is waiting for a person right now.</p>}
          </CardContent>
        </Card>
        {selected ? (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Avatar className="size-10">
                  <AvatarFallback className="text-[11px] bg-stone-200 text-stone-700">{initials(selected.conversation.contactName)}</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle>{selected.conversation.contactName}</CardTitle>
                  <CardDescription className="text-xs">{selected.conversation.controlMode === "takeover_pending" ? "Automated message in flight → Human" : `${selected.conversation.controlMode === "automation" ? "Journey" : "Team"} → Human handover`}</CardDescription>
                </div>
                <Badge variant="secondary" className="ml-auto">
                  {selected.conversation.controlMode === "automation" ? "Paused" : "Waiting"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-3">Why {selected.conversation.contactName.split(" ")[0]} needs help</Label>
                <div className="soft-panel">
                  <p>{selected.lastCustomerMessage ? `“${selected.lastCustomerMessage}”` : selected.reason}</p>
                  <span className="mt-3 block text-[11px] text-muted-foreground">
                    {selected.reason}
                    {selected.lastCustomerMessageAt ? ` · ${formatDateTime(selected.lastCustomerMessageAt)}` : ""}
                  </span>
                </div>
              </div>
              <div className="core-hint">
                <ShieldCheck className="mt-0.5 size-4 shrink-0" />
                <div>
                  <strong className="mb-1 block">Automation pauses at handover</strong>
                  Accepting takes ownership: queued automated messages are suppressed and any message already in flight is shown as such.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>Assign to</Label>
                  <Select value={assignee} onValueChange={setAssignee}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {team.members.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status after handover</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as "open" | "pending")}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="handover-note">Internal handover note</Label>
                <Textarea id="handover-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Context for whoever picks this up" />
              </div>
              <p className="flex gap-2 text-xs text-muted-foreground">
                <ShieldCheck size={16} />
                The journey stays paused until an agent explicitly releases it.
              </p>
              <div className="flex justify-end border-t pt-5">
                <Button disabled={!assignee || accept.isPending} onClick={() => accept.mutate()}>
                  Accept and open conversation
                  <ArrowRight />
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">Handovers appear here when a journey, a moderation rule or a teammate asks for a person.</CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
